/**
 * Damage, accuracy, defeat and experience, basic attacks, skills and
 * charges. The formulas are on the wiki page Design → Combat rules; the order
 * a skill resolves in is on Architecture → game-core contract, section Skill
 * resolution. Every number comes from `ctx.content.rules`.
 */
import type { ContentPack, Element, ItemAuto, Skill, Targeting } from '@ab/content-schema';
import { type ApplyResult, type Ctx, type Step, chain, none } from './commands/types.ts';
import { lookup } from './content.ts';
import { idiv, percent } from './determinism.ts';
import { type EffectSource, applyEffect, assertEscapeAllowed, assertRunnable, assertSomeoneToHeal } from './effects.ts';
import type { DamageCause, Event } from './events.ts';
import { gridView } from './floorview.ts';
import { type Dir, type ResolvedTarget, TargetingError, add, cornerClear, dirOffset, inBounds, resolveTargets } from './grid.ts';
import { type DamageContext, type Style, applyTriggerEffects, damageTakenMod, onFatalDamage, runTrigger, skipsCharge } from './passives.ts';
import { type RngState, roll } from './rng.ts';
import { faceUnit } from './round.ts';
import { type BagEntry, InvalidCommand, type RunState, type Unit, findUnit, hasStatus, leaderOf, takeOneFromBag, unitById, updateUnit, withRng } from './state.ts';
import { effectiveStat, expForDefeat, expToNext, speciesOf, typesOf, unitStat } from './stats.ts';
import { wakeUp } from './status.ts';

export type DamageInput = {
  readonly level: number;
  readonly power: number;
  readonly atk: number;
  readonly def: number;
  /** Type chart percent against the defender (both types already combined). */
  readonly typePct: number;
  readonly stabPct: number;
};

/**
 * `base = ((2 × L ÷ 5 + 2) × P × atk ÷ def) ÷ 50 + 2`, integer divisions left
 * to right; then type chart and STAB as integer percents in that order;
 * minimum 1 unless the type multiplier is 0. A defence of 0 counts as 1.
 * Falloff is not a multiplier: a `line` skill's power is reduced (in power
 * points) before this formula, and its accuracy (in percent points).
 */
export const damageFormula = (i: DamageInput): number => {
  const inner = idiv((idiv(2 * i.level, 5) + 2) * i.power * i.atk, Math.max(1, i.def));
  const base = idiv(inner, 50) + 2;
  if (i.typePct === 0) return 0;
  return Math.max(1, percent(percent(base, i.typePct), i.stabPct));
};

/**
 * The type chart percent of `element` (null = typeless) against a defender's
 * types. A two-type defender multiplies both: the product is one integer
 * percent, `p1 × p2 ÷ 100`. Typeless is 100 except `typelessVsImaginary`.
 */
export const typePercent = (ctx: Ctx, element: Element | null, defenderTypes: readonly Element[]): number => {
  const { typeChart, combat } = ctx.content.rules;
  const one = (t: Element): number =>
    element === null ? (t === 'imaginary' ? combat.typelessVsImaginary : 100) : (typeChart[element]?.[t] ?? 100);
  let pct = 100;
  for (const t of defenderTypes) pct = percent(pct, one(t));
  return pct;
};

/** `stab` when the element matches one of the attacker's types, else 100. Typeless is always 100. */
export const stabPercent = (ctx: Ctx, element: Element | null, attackerTypes: readonly Element[]): number =>
  element !== null && attackerTypes.includes(element) ? ctx.content.rules.combat.stab : 100;

/**
 * The speed term of a skill's accuracy roll, in percent points: `clamp((attacker
 * spd − defender spd) × speedAccuracyPerPoint, −speedAccuracyCap,
 * +speedAccuracyCap)`, with effective (staged) spd, when the two are on
 * different sides; 0 between units of the same side (an ally does not dodge a
 * Heal).
 */
export const speedAccuracyBonus = (content: ContentPack, attacker: Unit, defender: Unit): number => {
  if (attacker.side === defender.side) return 0;
  const { speedAccuracyPerPoint, speedAccuracyCap } = content.rules.combat;
  const diff = effectiveStat(content, attacker, 'spd') - effectiveStat(content, defender, 'spd');
  return Math.max(-speedAccuracyCap, Math.min(speedAccuracyCap, diff * speedAccuracyPerPoint));
};

/**
 * The percent chance of a skill to hit before the roll: `clamp(accuracy +
 * speedAccuracyBonus, 0, 100)`, then × `blindAccuracy`% if the attacker is
 * blind.
 */
export const hitChance = (content: ContentPack, attacker: Unit, defender: Unit, accuracy: number): number => {
  const chance = Math.max(0, Math.min(100, accuracy + speedAccuracyBonus(content, attacker, defender)));
  return hasStatus(attacker, 'blind') ? percent(chance, content.rules.statuses.blindAccuracy) : chance;
};

/**
 * The percent chance of a basic attack to hit: 100 (no roll, no draw), or
 * `blindAccuracy` when the attacker is blind. Speed does not apply.
 */
export const basicHitChance = (content: ContentPack, attacker: Unit): number =>
  hasStatus(attacker, 'blind') ? content.rules.statuses.blindAccuracy : 100;

/**
 * Rolls to hit on the `combat` stream with `chance` (a chance of 100 draws
 * nothing). Against a stunned defender there is no roll at all: it hits and
 * draws nothing.
 */
const rollToHit = (state: RunState, defender: Unit, chance: number): { hit: boolean; state: RunState } => {
  if (hasStatus(defender, 'stun')) return { hit: true, state };
  const r = roll(state.rng.combat, chance);
  return { hit: r.hit, state: withRng(state, 'combat', r.next) };
};

/** A skill's accuracy roll: `hitChance` through `rollToHit` (no roll against a stunned defender). */
export const accuracyRoll = (ctx: Ctx, state: RunState, attacker: Unit, defender: Unit, accuracy: number): { hit: boolean; state: RunState } =>
  rollToHit(state, defender, hitChance(ctx.content, attacker, defender, accuracy));

export const maxHp = (ctx: Ctx, u: Unit): number => unitStat(ctx.content, u, 'hp');

export const maxAp = (ctx: Ctx, u: Unit): number => unitStat(ctx.content, u, 'ap');

// ---------------------------------------------------------------- experience

/** The learnset skills of the unit's new level (replacing the oldest when full). A human has no learnset: its skills are its weapon's. */
const learnAt = (ctx: Ctx, u: Unit, events: Event[]): Unit => {
  if (u.kind === 'human') return u;
  const slots = ctx.content.rules.dungeon.skillSlots;
  let skills = [...u.skills];
  for (const e of speciesOf(ctx.content, u.monster).learnset) {
    if (e.level !== u.level || skills.includes(e.skill)) continue;
    skills = [...(skills.length >= slots ? skills.slice(skills.length - slots + 1) : skills), e.skill];
    events.push({ type: 'skill_learned', unit: u.id, skill: e.skill });
  }
  return { ...u, skills };
};

/** One level up: current HP and AP rise by the gain in their maximum, then that level's learnset skills. */
const levelUp = (ctx: Ctx, u: Unit, events: Event[]): Unit => {
  const next = u.level + 1;
  const gain = (stat: 'hp' | 'ap'): number => unitStat(ctx.content, { ...u, level: next }, stat) - unitStat(ctx.content, u, stat);
  const raised: Unit = { ...u, level: next, exp: u.exp - expToNext(ctx.content, u.level), hp: u.hp + gain('hp'), ap: u.ap + gain('ap') };
  events.push({ type: 'level_up', unit: u.id, level: next });
  return learnAt(ctx, raised, events);
};

/** Adds exp and levels up while exp reaches `expToNext`, up to `levelCap`. */
export const gainExp = (ctx: Ctx, state: RunState, unitId: string, amount: number): ApplyResult => {
  const events: Event[] = [{ type: 'exp_gained', unit: unitId, amount }];
  let u: Unit = { ...unitById(state, unitId), exp: unitById(state, unitId).exp + amount };
  while (u.level < ctx.content.rules.combat.levelCap && u.exp >= expToNext(ctx.content, u.level)) u = levelUp(ctx, u, events);
  const done = u;
  return { state: updateUnit(state, unitId, () => done), events };
};

/** Every standing party unit gains the exp for defeating an enemy of `level`, in party order. */
const grantDefeatExp = (ctx: Ctx, state: RunState, level: number): ApplyResult =>
  chain(
    state,
    ...state.party.map((id) => (s: RunState): ApplyResult => {
      const u = findUnit(s, id);
      return u === null || u.down ? none(s) : gainExp(ctx, s, id, expForDefeat(ctx.content, level));
    }),
  );

// ---------------------------------------------------------------- damage and defeat

/**
 * The boss has fallen: the run completes at once, as if the party left the
 * dungeon. Enemies are removed, the floor is cleared, phase `completed`,
 * `run_completed`; the party, bag and `egg` are kept. The round stops after
 * this unit's turn (round.ts), and the rest of the use that felled the boss
 * does nothing (every step checks the phase).
 */
const completeRun = (state: RunState): ApplyResult => ({
  state: { ...state, phase: 'completed', units: state.units.filter((x) => x.side === 'party'), floor: null },
  events: [{ type: 'run_completed' }],
});

/**
 * The defeat roll: each bag stack, in bag order, is lost with
 * `defeatItemLossChance`% on the `loot` stream. Emits `items_lost` with the
 * lost stacks (possibly none). An egg is never lost (a run that obtained one has
 * already completed, and an egg carried in is kept).
 */
const loseItems = (ctx: Ctx, state: RunState): ApplyResult => {
  const { kept, lost, next } = defeatLoss(ctx.content, state.bag, state.rng.loot);
  return { state: { ...withRng(state, 'loot', next), bag: kept }, events: [{ type: 'items_lost', items: lost }] };
};

/**
 * The defeat roll on a bag, alone: each stack, in bag order, is lost with
 * `defeatItemLossChance`% drawn from `rng` (a `loot` stream); the stacks kept,
 * the stacks lost and the stream after the draws. `loseItems` rolls it on the
 * run's own `loot` stream; the server rolls it for a run given up mid-floor
 * on the stream that floor's seed starts (`seedStreams(seed).loot`, ADR 0039
 * D3), so giving up loses what failing would.
 */
export const defeatLoss = (content: ContentPack, bag: readonly BagEntry[], rng: RngState): { kept: BagEntry[]; lost: BagEntry[]; next: RngState } => {
  let next = rng;
  const kept: BagEntry[] = [];
  const lost: BagEntry[] = [];
  for (const entry of bag) {
    const r = roll(next, content.rules.dungeon.defeatItemLossChance);
    next = r.next;
    (r.hit ? lost : kept).push(entry);
  }
  return { kept, lost, next };
};

/**
 * The run fails: phase `failed`, `run_failed`, then the defeat roll on the bag
 * (`loseItems`). Called by the round's outcome when the leader is down, and by
 * `defeat` when the boss falls while the leader is down (the round stops there).
 */
export const failRun = (ctx: Ctx, state: RunState): ApplyResult =>
  chain(
    { ...state, phase: 'failed' },
    (s) => ({ state: s, events: [{ type: 'run_failed' }] }),
    (s) => loseItems(ctx, s),
  );

/** The `auto` block of a bag item that acts when a party unit falls (a `charm` with `auto.when` `party_unit_falls`), else null. */
const fallAuto = (ctx: Ctx, item: string): ItemAuto | null => {
  const def = lookup(ctx.content.items, item);
  return def?.category === 'charm' && def.auto?.when === 'party_unit_falls' ? def.auto : null;
};

/**
 * A charm acts for a party unit that has just gone down (decided 2026-09-25,
 * Q35: the Revive Orb): the first bag stack, in bag order, whose item is a
 * charm acting on `party_unit_falls` loses one (the stack goes at 0),
 * `item_auto_used`, then the charm's `auto.effects` apply to the fallen unit
 * in order (the orb's `revive`: effects.ts). One charm per fall; nothing when
 * the bag holds none.
 */
const fallCharm = (ctx: Ctx, state: RunState, unitId: string): ApplyResult => {
  const entry = state.bag.find((e) => fallAuto(ctx, e.item) !== null);
  const auto = entry === undefined ? null : fallAuto(ctx, entry.item);
  if (entry === undefined || auto === null) return none(state);
  const u = unitById(state, unitId);
  const source: EffectSource = { unit: unitId, style: null, element: null, falloffPower: 0, origin: 'item' };
  const target: ResolvedTarget = { unit: unitId, pos: u.pos, distance: 0 };
  return chain(
    takeOneFromBag(state, entry.item),
    (s) => ({ state: s, events: [{ type: 'item_auto_used', unit: unitId, item: entry.item }] }),
    ...auto.effects.map((e): Step => (s) => applyEffect(ctx, s, e, source, target)),
  );
};

/**
 * A unit falls. A charge it held is interrupted. Party units go down (hp 0,
 * statuses, stages and charge cleared) and stay in the list; then a charm in
 * the bag may bring one back at once (`fallCharm`: the Revive Orb), so the
 * round's outcome sees it standing. Enemies are
 * removed and every standing party unit gains exp. A boss falling also emits
 * `boss_defeated` right after `unit_defeated`. If the leader stands,
 * `egg_obtained` follows, the run's `egg` is set to the boss's species, and
 * after the exp the run completes at once (`completeRun`). If the leader is
 * down (it fell earlier in the round and an ally or a burn felled the boss),
 * there is no egg: after the exp the run fails at once (`failRun`).
 */
export const defeat = (ctx: Ctx, state: RunState, unitId: string): ApplyResult => {
  const u = unitById(state, unitId);
  const events: Event[] = [];
  if (u.charging !== null) events.push({ type: 'charge_interrupted', unit: u.id, skill: u.charging.skill });
  events.push({ type: 'unit_defeated', unit: u.id });
  if (u.side === 'party') {
    const s = updateUnit(state, unitId, (x) => ({ ...x, hp: 0, down: true, statuses: [], stages: [], charging: null }));
    const charm = fallCharm(ctx, s, unitId);
    return { state: charm.state, events: [...events, ...charm.events] };
  }
  const boss = u.boss !== null;
  const won = boss && !leaderOf(state).down;
  if (boss) events.push({ type: 'boss_defeated', unit: u.id });
  if (won) events.push({ type: 'egg_obtained', monster: u.monster });
  const removed: RunState = { ...state, egg: won ? u.monster : state.egg, units: state.units.filter((x) => x.id !== unitId) };
  const exp = chain(
    removed,
    (s) => grantDefeatExp(ctx, s, u.level),
    (s) => (won ? completeRun(s) : boss ? failRun(ctx, s) : none(s)),
  );
  return { state: exp.state, events: [...events, ...exp.events] };
};

/**
 * Lowers a unit's hp by `amount` (never below 0), marks it as having taken
 * damage this turn, emits `damaged` with the hp actually lost, and defeats it
 * at 0. `source` is the attacking unit, or null for status damage; `style` is
 * null for status damage; `cause` names the status (`burn`, `poison`) for a
 * tick and is null for damage from a unit. The holder's passives take part, in this order:
 * 1. `always` `damage_taken_mod` whose `if` holds and whose `style` / `element`
 *    match: `amount × (100 + sum)%`, minimum 1 unless the sum is −100 or less
 *    (`passive_triggered`);
 * 2. when the hit would take hp to 0: `on_fatal_damage` triggers, `if`
 *    evaluated before the damage; `survive_fatal` leaves `hpLeft` hp;
 * 3. `damaged`. Damage never wakes a sleeper here: a basic attack, skill or
 *    item woke its target when the hit landed (`wakeUp` in status.ts), once
 *    per hit and before its effects, so a hit that applies sleep and then
 *    damage leaves the target asleep;
 * 4. if the holder still stands and lost hp: `on_damage_taken` triggers, `if`
 *    evaluated after the damage, then the other effects of the fatal triggers
 *    that fired; at 0 hp the holder is defeated instead.
 */
export const dealDamage = (
  ctx: Ctx,
  state: RunState,
  targetId: string,
  amount: number,
  source: string | null,
  element: Element | null,
  style: Style | null,
  cause: DamageCause = null,
): ApplyResult => {
  const target = findUnit(state, targetId);
  if (target === null || target.down) return none(state);
  const context: DamageContext = { element, style };
  const events: Event[] = [];
  let s = state;
  let dealt = amount;
  const mod = damageTakenMod(ctx, target, context);
  if (mod.passive !== null && amount > 0) {
    dealt = 100 + mod.sum <= 0 ? 0 : Math.max(1, percent(amount, 100 + mod.sum));
    events.push({ type: 'passive_triggered', unit: targetId, passive: mod.passive });
  }
  let hpFloor = 0;
  let fatalSteps: Step[] = [];
  if (dealt > 0 && dealt >= target.hp) {
    const fatal = onFatalDamage(ctx, s, targetId, context);
    s = fatal.result.state;
    events.push(...fatal.result.events);
    hpFloor = fatal.hpFloor;
    fatalSteps = fatal.fired.map((t): Step => (x) => applyTriggerEffects(ctx, x, targetId, t));
  }
  const hp = Math.max(hpFloor, target.hp - dealt);
  const lost = target.hp - hp;
  s = updateUnit(s, targetId, (u) => ({ ...u, hp, tookDamageThisTurn: u.tookDamageThisTurn || lost > 0 }));
  events.push({ type: 'damaged', unit: targetId, amount: lost, hp, source, element, cause });
  const after =
    hp > 0
      ? chain(s, (x) => (lost > 0 ? runTrigger(ctx, x, targetId, 'on_damage_taken', context) : none(x)), ...fatalSteps)
      : defeat(ctx, s, targetId);
  return { state: after.state, events: [...events, ...after.events] };
};

// ---------------------------------------------------------------- basic attack

/** Adds SP up to `spMax`; emits `sp_changed` only when it changed. */
export const gainSp = (ctx: Ctx, state: RunState, unitId: string, amount: number): ApplyResult => {
  const u = findUnit(state, unitId);
  if (u === null || u.down) return none(state);
  const sp = Math.min(ctx.content.rules.combat.spMax, u.sp + amount);
  if (sp === u.sp) return none(state);
  return {
    state: updateUnit(state, unitId, (x) => ({ ...x, sp })),
    events: [{ type: 'sp_changed', unit: unitId, delta: sp - u.sp, sp }],
  };
};

/** The basic attack's roll (`basicHitChance`: none unless the attacker is blind); on a hit the target wakes if asleep (`wakeUp`), then takes the basic attack's damage. */
const basicHit = (ctx: Ctx, state: RunState, attackerId: string, targetId: string): ApplyResult => {
  const attacker = unitById(state, attackerId);
  const acc = rollToHit(state, unitById(state, targetId), basicHitChance(ctx.content, attacker));
  if (!acc.hit) return { state: acc.state, events: [{ type: 'missed', unit: attackerId, target: targetId }] };
  const woke = wakeUp(acc.state, targetId);
  const target = unitById(woke.state, targetId);
  const amount = damageFormula({
    level: attacker.level,
    power: ctx.content.rules.combat.basicAttackPower,
    atk: effectiveStat(ctx.content, attacker, 'patk'),
    def: effectiveStat(ctx.content, target, 'pdef'),
    typePct: typePercent(ctx, null, typesOf(ctx.content, target)),
    stabPct: stabPercent(ctx, null, typesOf(ctx.content, attacker)),
  });
  const dealt = dealDamage(ctx, woke.state, targetId, amount, attackerId, null, 'physical');
  return { state: dealt.state, events: [...woke.events, ...dealt.events] };
};

/**
 * A basic attack on the tile in the attacker's facing direction (diagonals
 * included). A diagonal attack follows the corner rule of a diagonal step
 * (grid.ts `cornerClear`: both orthogonal neighbours of the attacker are
 * non-wall); when a wall blocks the corner the attack hits the air, whoever
 * stands on the diagonal tile. Empty air: `attacked` with a null target and
 * nothing else. A hit that lands wakes a sleeping target before its damage. A unit there: no accuracy roll unless the attacker is
 * blind (`basicHitChance`; speed never applies; no roll against a stunned
 * target), physical damage
 * with `basicAttackPower`, typeless for every unit, a human included (its
 * weapon's elemental damage comes from the weapon's skills; decided
 * 2026-09-25, Q34), and, when the target is on the other side,
 * `spPerBasicAttack` SP, hit or miss. Hitting an ally gives no SP, so SP
 * cannot be farmed off the party. An attack reaches an ally only when the
 * attacker is confused (round.ts `attackUnit`): a leader's `attack` into an
 * ally is otherwise refused before the round, and the AI attacks only foes.
 */
export const basicAttack = (ctx: Ctx, state: RunState, attackerId: string): ApplyResult => {
  const attacker = unitById(state, attackerId);
  const g = gridView(state);
  const pos = add(attacker.pos, dirOffset[attacker.facing]);
  const targetId = inBounds(g, pos) && cornerClear(g, attacker.pos, attacker.facing) ? g.unitAt(pos) : null;
  const attacked: Event = { type: 'attacked', unit: attackerId, target: targetId };
  if (targetId === null) return { state, events: [attacked] };
  const foe = unitById(state, targetId).side !== attacker.side;
  return chain(
    state,
    (s) => ({ state: s, events: [attacked] }),
    (s) => basicHit(ctx, s, attackerId, targetId),
    (s) => (foe && s.phase === 'playing' ? gainSp(ctx, s, attackerId, ctx.content.rules.combat.spPerBasicAttack) : none(s)),
  );
};

// ---------------------------------------------------------------- targeting for skills and items

/**
 * The units a targeting block hits for `user` (its position and facing as
 * given). `target` is required for shape `single` (a standing unit) and
 * forbidden otherwise. Targeting errors throw `InvalidCommand`.
 */
export const resolveFor = (state: RunState, user: Unit, targeting: Targeting, target: string | null): ResolvedTarget[] => {
  if (targeting.shape === 'single') {
    if (target === null) throw new InvalidCommand('needs_target', 'this targeting needs a target');
    const t = findUnit(state, target);
    if (t === null || t.down) throw new InvalidCommand('no_standing_unit', `no standing unit ${target}`, { unit: target });
  } else if (target !== null) {
    throw new InvalidCommand('takes_no_target', `shape ${targeting.shape} takes no target`);
  }
  try {
    return resolveTargets(
      gridView(state),
      targeting,
      user,
      target,
      (id) => unitById(state, id).pos,
      (id) => unitById(state, id).side === user.side,
    );
  } catch (e) {
    if (e instanceof TargetingError) throw new InvalidCommand(e.code, e.message);
    throw e;
  }
};

/**
 * The units a `single` targeting block may pick for `userId` from where it
 * stands: standing, allowed by `who` (none given: the user only), within
 * Manhattan `range` and reachable within it (`reachableWithin`), in id order.
 * The same test `resolveFor` applies, so a client can list candidates without
 * repeating the rule. Empty for any other shape.
 */
export const singleTargets = (state: RunState, userId: string, targeting: Targeting): Unit[] => {
  const user = findUnit(state, userId);
  if (user === null || user.down || targeting.shape !== 'single') return [];
  return state.units.filter((t) => {
    if (t.down) return false;
    try {
      resolveFor(state, user, targeting, t.id);
      return true;
    } catch (e) {
      if (e instanceof InvalidCommand) return false;
      throw e;
    }
  });
};

/**
 * The units a skill or an item (`id`: a skill id, or an item id for the
 * item's `use.targeting`) would reach if `userId` used it now facing `dir`,
 * for a shape that takes no target (`line`, `area`, `room`, `self`, `floor`):
 * the unit ids `resolveFor` gives, in targeting order, whichever side they
 * are on. Null for a `single` shape, an unknown id, a user that is gone or
 * down, or a targeting that would be refused (a `line` on a diagonal). Only
 * the targeting: whether the skill or item can be used is `skillUsability` /
 * `itemUsability` and the command itself. A client asks this to warn before
 * a use that reaches nobody.
 */
export const previewTargets = (content: ContentPack, state: RunState, userId: string, id: string, dir: Dir): string[] | null => {
  const user = findUnit(state, userId);
  const targeting = lookup(content.skills, id)?.targeting ?? lookup(content.items, id)?.use?.targeting;
  if (user === null || user.down || targeting === undefined || targeting.shape === 'single') return null;
  try {
    return resolveFor(state, { ...user, facing: dir }, targeting, null).map((t) => t.unit);
  } catch (e) {
    if (e instanceof InvalidCommand) return null;
    throw e;
  }
};

// ---------------------------------------------------------------- skills and charging

/** What a checked skill use will do: fire now, or start a charge. `skippedBy` names a `skip_charge` passive that removed the charge. */
export type SkillPlan = {
  readonly skill: Skill;
  readonly charge: boolean;
  readonly skippedBy: string | null;
  /** The targets it would hit if it fired now. */
  readonly targets: readonly ResolvedTarget[];
};

/**
 * The checks of `checkSkillUse` that come before targeting, in order: the user
 * stands; knows the skill; every effect is runnable; holds no charge (unless
 * `fromCharge`); can pay `cost.ap` and `cost.sp`. Throws `InvalidCommand`;
 * returns the skill.
 */
const skillBeforeTargeting = (ctx: Ctx, state: RunState, userId: string, skillId: string, fromCharge: boolean): { readonly u: Unit; readonly skill: Skill } => {
  const u = findUnit(state, userId);
  if (u === null || u.down) throw new InvalidCommand('unit_cannot_act', `${userId} cannot act`, { unit: userId });
  if (!u.skills.includes(skillId)) throw new InvalidCommand('skill_not_known', `${userId} does not know ${skillId}`, { unit: userId, skill: skillId });
  const skill = lookup(ctx.content.skills, skillId);
  if (skill === null) throw new InvalidCommand('unknown_skill', `unknown skill ${skillId}`, { skill: skillId });
  assertRunnable(skill.effects, skill.style ?? null);
  if (!fromCharge && u.charging !== null) throw new InvalidCommand('unit_charging', `${userId} is charging`, { unit: userId });
  if ((skill.cost.ap ?? 0) > u.ap) throw new InvalidCommand('not_enough_ap', `not enough AP for ${skillId}`, { skill: skillId });
  if ((skill.cost.sp ?? 0) > u.sp) throw new InvalidCommand('not_enough_sp', `not enough SP for ${skillId}`, { skill: skillId });
  return { u, skill };
};

/**
 * Whether `userId` could use `skillId` now, ignoring targeting: checks 1 to 5
 * of `checkSkillUse` (`skillBeforeTargeting`). Returns the `InvalidCommand`
 * message, or null when they pass. A client asks this before prompting for a
 * direction or a target; `apply` still checks the whole command.
 */
export const skillUsability = (content: ContentPack, state: RunState, userId: string, skillId: string): string | null => {
  try {
    skillBeforeTargeting({ content }, state, userId, skillId, false);
    return null;
  } catch (e) {
    if (e instanceof InvalidCommand) return e.message;
    throw e;
  }
};

/**
 * Checks, in order, that `userId` may use `skillId` facing `facing` at
 * `target`, and throws `InvalidCommand` otherwise: the user stands; knows the
 * skill; every effect is runnable (no `custom`); holds no charge (unless
 * this is the charge firing); can pay `cost.ap` and `cost.sp`; the
 * targeting resolves; an `escape` effect is used by the leader on itself
 * alone (`assertEscapeAllowed`); a heal has someone to heal
 * (`assertSomeoneToHeal`). Then whether it charges: aetherial, no `noCharge`,
 * `aetherialChargeTurns` > 0 and no `skip_charge` passive; a disrupted unit
 * cannot start a charge. `fromCharge` skips the charge checks. Changes nothing.
 */
export const checkSkillUse = (
  ctx: Ctx,
  state: RunState,
  userId: string,
  skillId: string,
  facing: Dir,
  target: string | null,
  fromCharge: boolean,
): SkillPlan => {
  const { u, skill } = skillBeforeTargeting(ctx, state, userId, skillId, fromCharge);
  const targets = resolveFor(state, { ...u, facing }, skill.targeting, target);
  assertEscapeAllowed(state, userId, targets, skill.effects);
  assertSomeoneToHeal(ctx, state, targets, skill.effects);
  const wantsCharge = !fromCharge && skill.style === 'aetherial' && skill.noCharge !== true && ctx.content.rules.combat.aetherialChargeTurns > 0;
  if (!wantsCharge) return { skill, charge: false, skippedBy: null, targets };
  const skippedBy = skipsCharge(ctx, u, 'aetherial');
  if (skippedBy !== null) return { skill, charge: false, skippedBy, targets };
  if (hasStatus(u, 'disrupt')) throw new InvalidCommand('disrupted_cannot_charge', `${userId} is disrupted and cannot charge`, { unit: userId });
  return { skill, charge: true, skippedBy: null, targets };
};

/** Spends a skill's cost: `ap_changed` / `sp_changed` with a negative delta. */
const spend = (state: RunState, userId: string, skill: Skill): ApplyResult => {
  const ap = skill.cost.ap ?? 0;
  const sp = skill.cost.sp ?? 0;
  const u = unitById(state, userId);
  const events: Event[] = [];
  if (ap > 0) events.push({ type: 'ap_changed', unit: userId, delta: -ap, ap: u.ap - ap });
  if (sp > 0) events.push({ type: 'sp_changed', unit: userId, delta: -sp, sp: u.sp - sp });
  return { state: updateUnit(state, userId, (x) => ({ ...x, ap: x.ap - ap, sp: x.sp - sp })), events };
};

/**
 * One target of a skill: accuracy `accuracy − falloff.accuracy × distance`
 * (minimum 0), then `accuracyRoll` (speed, blind, the cap at 100; no roll
 * against a stunned target) on the `combat` stream; a miss emits `missed`; on
 * a hit the target wakes if asleep (status.ts `wakeUp`, whether or not the
 * skill deals damage), then every effect applies in order. Nothing once the
 * run has ended (an escape or the boss's defeat earlier in the same use).
 */
const hitTarget = (ctx: Ctx, state: RunState, userId: string, skill: Skill, rt: ResolvedTarget): ApplyResult => {
  const u = findUnit(state, userId);
  const t = findUnit(state, rt.unit);
  if (state.phase !== 'playing' || u === null || u.down || t === null || t.down) return none(state);
  const falloff = skill.targeting.falloff;
  const acc = accuracyRoll(ctx, state, u, t, Math.max(0, skill.accuracy - (falloff?.accuracy ?? 0) * rt.distance));
  if (!acc.hit) return { state: acc.state, events: [{ type: 'missed', unit: userId, target: rt.unit }] };
  const source: EffectSource = { unit: userId, style: skill.style ?? null, element: skill.element ?? null, falloffPower: falloff?.power ?? 0, origin: 'skill' };
  return chain(
    acc.state,
    (s) => wakeUp(s, rt.unit),
    ...skill.effects.map((e): Step => (s) => applyEffect(ctx, s, e, source, rt)),
  );
};

/** Fires a checked skill: `passive_triggered` if a passive removed the charge, the cost, `skill_used`, then each target in order. */
const fire = (ctx: Ctx, state: RunState, userId: string, skillId: string, plan: SkillPlan): ApplyResult => {
  const used: Event = { type: 'skill_used', unit: userId, skill: skillId, targets: plan.targets.map((t) => t.unit) };
  return chain(
    state,
    (s) => (plan.skippedBy === null ? none(s) : { state: s, events: [{ type: 'passive_triggered', unit: userId, passive: plan.skippedBy }] }),
    (s) => spend(s, userId, plan.skill),
    (s) => ({ state: s, events: [used] }),
    ...plan.targets.map((rt): Step => (s) => hitTarget(ctx, s, userId, plan.skill, rt)),
  );
};

/**
 * Uses a skill: checks it (`checkSkillUse`, throws `InvalidCommand`), faces
 * `facing` (`turned` if it changed), then either starts a charge
 * (`charge_started`, nothing spent) or fires. `fromCharge` is set only by
 * `chargeTurn`, which fires a charge that was already paid for by waiting.
 */
export const useSkill = (
  ctx: Ctx,
  state: RunState,
  userId: string,
  skillId: string,
  facing: Dir,
  target: string | null,
  fromCharge = false,
): ApplyResult => {
  const faced = faceUnit(state, userId, facing);
  const plan = checkSkillUse(ctx, faced.state, userId, skillId, facing, target, fromCharge);
  if (!plan.charge) {
    const fired = fire(ctx, faced.state, userId, skillId, plan);
    return { state: fired.state, events: [...faced.events, ...fired.events] };
  }
  const charging = { skill: skillId, facing, target, turnsLeft: ctx.content.rules.combat.aetherialChargeTurns };
  return {
    state: updateUnit(faced.state, userId, (x) => ({ ...x, charging })),
    events: [...faced.events, { type: 'charge_started', unit: userId, skill: skillId }],
  };
};

/**
 * Whether `unitId`'s charge was aimed at a foe that has left the skill's
 * shape: the charged skill can reach the other side (`who` `enemies` or
 * `everyone`), and its targeting, resolved as `checkSkillUse` would with the
 * stored facing and target (`resolveFor`), reaches no standing unit of the
 * other side (a `single` target that is gone, down, out of range or out of
 * reach reaches nobody). False for a unit that is not charging, and for a
 * charge that cannot reach a foe at all (a heal on an ally, a `self` skill).
 * round.ts cancels an ally's charge on this at the start of its turn
 * (decided 2026-09-25, Q32).
 */
export const chargeLostFoe = (ctx: Ctx, state: RunState, unitId: string): boolean => {
  const u = unitById(state, unitId);
  const c = u.charging;
  const skill = c === null ? null : lookup(ctx.content.skills, c.skill);
  if (c === null || skill === null) return false;
  const t = skill.targeting;
  if (t.shape === 'self' || (t.who !== 'enemies' && t.who !== 'everyone')) return false;
  try {
    return !resolveFor(state, { ...u, facing: c.facing }, t, c.target).some((rt) => unitById(state, rt.unit).side !== u.side);
  } catch (e) {
    if (e instanceof InvalidCommand) return true;
    throw e;
  }
};

/**
 * A charging unit's turn (round.ts calls this instead of its action). With
 * more than one turn left the unit stands still and the count drops by one.
 * Otherwise the charge clears and fires with the stored facing and target:
 * `charge_fired`, then the skill as `useSkill` resolves it. If the skill can
 * no longer be used (the target is gone, down or out of range, the cost
 * cannot be paid, or a heal has nobody left to heal) the charge fizzles: `charge_interrupted`, nothing spent.
 */
export const chargeTurn = (ctx: Ctx, state: RunState, unitId: string): ApplyResult => {
  const u = unitById(state, unitId);
  const c = u.charging;
  if (c === null) return none(state);
  if (c.turnsLeft > 1) return none(updateUnit(state, unitId, (x) => ({ ...x, charging: { ...c, turnsLeft: c.turnsLeft - 1 } })));
  const cleared = updateUnit(state, unitId, (x) => ({ ...x, charging: null }));
  try {
    checkSkillUse(ctx, cleared, unitId, c.skill, c.facing, c.target, true);
  } catch (e) {
    if (e instanceof InvalidCommand) return { state: cleared, events: [{ type: 'charge_interrupted', unit: unitId, skill: c.skill }] };
    throw e;
  }
  return chain(
    cleared,
    (s) => ({ state: s, events: [{ type: 'charge_fired', unit: unitId, skill: c.skill }] }),
    (s) => useSkill(ctx, s, unitId, c.skill, c.facing, c.target, true),
  );
};
